/**
 * Pro Features page - Dedicated page for Pro subscription features
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { subscriptionService } from '../services/subscriptionService';
import FamilySharing from '../components/FamilySharing';
import ReportsManager from '../components/ReportsManager';

const ProFeatures = () => {
  const navigate = useAppNavigation();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const result = await subscriptionService.getCurrentSubscription();
      setSubscription(result.subscription);
    } catch (err) {
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const isProUser = ['pro', 'organizer', 'mover_pro'].includes(subscription?.plan);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 3 }}>
      <Container maxWidth="lg">
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/app')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Pro Features
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!isProUser ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Unlock Pro Features
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              Upgrade to Pro to access family sharing, professional reports, and advanced features.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/app/subscription')}
            >
              Upgrade to Pro
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ p: 0 }}>
            <Tabs 
              value={tabValue} 
              onChange={(e, newValue) => setTabValue(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
            >
              <Tab label="Family Sharing" />
              <Tab label="Reports & Exports" />
            </Tabs>
            
            <Box sx={{ p: 3 }}>
              {tabValue === 0 && (
                <FamilySharing userSubscription={subscription} />
              )}
              
              {tabValue === 1 && (
                <ReportsManager userSubscription={subscription} />
              )}
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default ProFeatures;
